import type { EmbedImage, RichEmbed } from '@spawnwire/shared';
import { clsx } from '../../components/ui';
import { Markdown, type RenderContext } from '../../lib/markdown';
import { formatFull } from '../../lib/time';
import { ui } from '../../store/ui';

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

function EmbedPicture({ img, thumb }: { img: EmbedImage; thumb?: boolean }) {
  const max = thumb ? 80 : 400;
  const ratio = img.width && img.height ? Math.min(1, max / img.width, (thumb ? 80 : 300) / img.height) : 1;
  const w = img.width ? Math.round(img.width * ratio) : undefined;
  return (
    <button type="button" className={clsx('block overflow-hidden rounded', thumb ? 'shrink-0' : 'mt-2 max-w-full')} onClick={() => ui.open({ kind: 'lightbox', url: img.url, filename: img.filename })} aria-label={`Open image ${img.filename}`}>
      <img src={`${img.url}?preview=1`} alt={img.filename} loading="lazy" width={w} className={clsx('block object-contain', thumb ? 'max-h-20 max-w-20' : 'max-h-72 max-w-full')} style={w ? { width: w } : undefined} />
    </button>
  );
}

/** A bot's embed: a card with a coloured bar, title, text, fields, images and a footer. */
export function RichEmbedView({ e, ctx }: { e: RichEmbed; ctx: RenderContext }) {
  const link = (href: string | null, text: string, className: string) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer nofollow ugc" className={clsx(className, 'hover:underline')}>
        {text}
      </a>
    ) : (
      <span className={className}>{text}</span>
    );
  // Inline fields sit up to three to a row; a field that isn't inline takes a row to itself.
  return (
    <div className="flex w-full max-w-lg overflow-hidden rounded-md border border-line bg-sidebar" data-testid="rich-embed">
      <div className="w-1 shrink-0" style={{ backgroundColor: e.color !== null ? hex(e.color) : 'var(--color-accent)' }} aria-hidden />
      <div className="min-w-0 flex-1 px-3 py-2.5">
        <div className="flex gap-3">
          <div className="min-w-0 flex-1">
            {e.author && <div className="mb-1 text-xs font-semibold">{link(e.author.url, e.author.name, 'text-fg')}</div>}
            {e.title && <div className="font-semibold">{link(e.url, e.title, e.url ? 'text-link' : 'text-fg')}</div>}
            {e.description && (
              <div className="mt-1 whitespace-pre-wrap break-words text-sm text-fg">
                <Markdown content={e.description} ctx={ctx} />
              </div>
            )}
            {e.fields.length > 0 && (
              <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3">
                {e.fields.map((f, i) => (
                  <div key={i} className={clsx('min-w-0', !f.inline && 'sm:col-span-3')}>
                    <div className="text-xs font-semibold text-fg">{f.name}</div>
                    <div className="whitespace-pre-wrap break-words text-sm text-muted">
                      <Markdown content={f.value} ctx={ctx} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {e.thumbnail && <EmbedPicture img={e.thumbnail} thumb />}
        </div>
        {e.image && <EmbedPicture img={e.image} />}
        {(e.footer || e.timestamp) && (
          <div className="mt-2 text-xs text-faint">
            {e.footer?.text}
            {e.footer && e.timestamp && ' • '}
            {e.timestamp && <time dateTime={e.timestamp}>{formatFull(e.timestamp)}</time>}
          </div>
        )}
      </div>
    </div>
  );
}
